"use client";

import { QueryCache, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import type { ReactNode } from "react";

import { DialogProvider } from "@/components/app/dialogs/dialog-provider";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { ApolloProvider } from "@/contexts/apollo-context";
import { AuthContextProvider } from "@/contexts/auth-context";
import { FeedProvider } from "@/contexts/feed-context";
import { FocusMarketDataProvider } from "@/contexts/focus-market-data";
import { GatedContentProvider } from "@/contexts/gated-content-context";
import { LayoutProvider } from "@/contexts/layout-context";
import { NativeTranslateObserverContext } from "@/contexts/native-translate-observer-context";
import { PostCacheProvider } from "@/contexts/post-cache-context";
import { ScriptLoaderProvider } from "@/contexts/script-loader-context";
import { ThemeProvider } from "@/contexts/theme-context";
import { UserProvider } from "@/contexts/user-context";
import { configureDesoProtocol } from "@/utils/configure-deso-protocol";
import type { THEME_OPTIONS } from "@/utils/constants";
import {
  QUERY_CACHE_BUSTER,
  QUERY_CACHE_MAX_AGE_MS,
  queryPersister,
  shouldPersistQuery,
} from "@/utils/query-persistence";

configureDesoProtocol();

const NATIVE_BUTTON_WARNING_MATCHERS = [
  "Base UI: A component that acts as a button expected a native <button>",
  "Base UI: A component that acts as a button expected a non-<button>",
];

const shouldFilterNativeButtonConsoleError = (args: unknown[]) => {
  const text = args
    .map((arg) => {
      if (typeof arg === "string") {
        return arg;
      }

      if (arg instanceof Error) {
        return arg.message;
      }

      return "";
    })
    .join(" ");

  return (
    NATIVE_BUTTON_WARNING_MATCHERS.some((matcher) => text.includes(matcher)) ||
    (text.includes("React does not recognize the") &&
      text.includes("nativeButton") &&
      text.includes("DOM element"))
  );
};

const installNativeButtonConsoleFilter = () => {
  if (process.env.NODE_ENV !== "development" || typeof window === "undefined") {
    return;
  }

  const win = window as Window & {
    __focusNativeButtonConsoleFilterInstalled?: boolean;
  };

  if (win.__focusNativeButtonConsoleFilterInstalled) {
    return;
  }

  const originalConsoleError = console.error.bind(console);

  console.error = (...args) => {
    if (shouldFilterNativeButtonConsoleError(args)) {
      return;
    }

    originalConsoleError(...args);
  };

  win.__focusNativeButtonConsoleFilterInstalled = true;
};

installNativeButtonConsoleFilter();

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.suppressGlobalErrorToast) {
        console.error(error);
        return;
      }

      if (typeof window !== "undefined") {
        toast({
          variant: "destructive",
          title: "Error",
          description: `An error happened while processing your request`,
          details: createToastErrorDetails(error),
        });
      }
      console.error(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

const Providers = ({
  children,
  cookiePublicKeyFromServer,
  initialTheme,
  initialCustomColor,
}: {
  children: ReactNode;
  cookiePublicKeyFromServer: string;
  initialTheme: THEME_OPTIONS;
  initialCustomColor: string | null;
}) => {
  return (
    <NativeTranslateObserverContext>
      <ApolloProvider>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            buster: QUERY_CACHE_BUSTER,
            maxAge: QUERY_CACHE_MAX_AGE_MS,
            persister: queryPersister,
            dehydrateOptions: {
              shouldDehydrateQuery: shouldPersistQuery,
            },
          }}
        >
          <ThemeProvider
            initialCustomColor={initialCustomColor}
            initialTheme={initialTheme}
          >
            <FocusMarketDataProvider>
              <AuthContextProvider
                cookiePublicKeyFromServer={cookiePublicKeyFromServer}
              >
                <UserProvider>
                  <GatedContentProvider>
                    <LayoutProvider>
                      <FeedProvider>
                        <PostCacheProvider>
                          <ScriptLoaderProvider>
                            {children}
                            <DialogProvider />
                          </ScriptLoaderProvider>
                        </PostCacheProvider>
                      </FeedProvider>
                    </LayoutProvider>
                  </GatedContentProvider>
                </UserProvider>
              </AuthContextProvider>
            </FocusMarketDataProvider>
          </ThemeProvider>
        </PersistQueryClientProvider>
      </ApolloProvider>
    </NativeTranslateObserverContext>
  );
};

export default Providers;
